import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useItems } from '../contexts/ItemsContext';
import { useUser } from '../contexts/UserContext';
import { getRecommendationsByItemId } from '../api/recommendationAPI';
import { Heart, ChevronLeft, ChevronRight, Video , Users, Clock, Calendar, Cake} from 'lucide-react';
import './ItemDetail.css';
import MiniCart from '../components/MiniCart';
import AuthModal from '../components/AuthModal';

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getItemById } = useItems();
  const { user, addToCart, addFavorite, removeFavorite } = useUser();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const loadItem = async () => {
      try {
        setLoading(true);
        const itemData = await getItemById(id);
        console.log('getItemById result:', itemData);
        if (itemData) {
          setItem(itemData);
        } else {
          setError('למה זה לא עובד? לא ברור');
        }
      } catch (err) {
        setError('למה זה לא עובד? לא ברור');
        console.error('Error loading item:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadItem();
    }
  }, [id, getItemById]);

  useEffect(() => {
    const loadRecommendations = async () => {
      if (!id) return;
      try {
        setRecommendationsLoading(true);
        const data = await getRecommendationsByItemId(id);
        if (Array.isArray(data)) {
          setRecommendations(data);
        }
      } catch (err) {
        console.error('Error loading recommendations:', err);
      } finally {
        setRecommendationsLoading(false);
      }
    };

    if (item) {
      loadRecommendations();
    }
  }, [id, item]);

  const handleAddToCart = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      await addToCart(item);
      setShowSuccessMessage(true);
      setShowMiniCart(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      setTimeout(() => setShowMiniCart(false), 6000);
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  const isItemFavorite = () => {
    if (!user?.likes || !item) return false;
    return user.likes.some(like => like.id === item.id);
  };

  const toggleFavorite = async () => {
    if (!user) return;

    try {
      if (isItemFavorite()) {
        await removeFavorite(item.id);
      } else {
        await addFavorite(item);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const getItemImages = () => {
    if (!item?.files) return [];
    return item.files.filter(file => file.type === 'image');
  };

  const nextImage = () => {
    const images = getItemImages();
    if (images.length > 0) {
      setSelectedImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    const images = getItemImages();
    if (images.length > 0) {
      setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const handleBackClick = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      const genreMap = {
        'תוכנית': '/programs',
        'מיניטס': '/minites',
        'מיתוג': '/branding'
      };
      navigate(genreMap[item?.genre] || '/programs');
    }
  };

  if (loading) {
    return (
      <div className="item-detail-container">
        <div className="item-detail-loading">טוען מוצר...</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="item-detail-container">
        <div className="item-detail-error">
          <h2>שגיאה</h2>
          <p>{'למה זה לא עובד? לא ברור'}</p>
          <button onClick={handleBackClick} className="back-btn">
            מכאן חוזרים
          </button>
        </div>
      </div>
    );
  }

  console.log('Item age:', item.age, 'item:', item);

  const images = getItemImages();
  const mainImage = images[selectedImageIndex] || images[0];

  return (
    <div className='out-container'>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      <MiniCart
        isOpen={showMiniCart}
        items={user?.cart || []}
        onClose={() => setShowMiniCart(false)}
        onGoToCart={() => { setShowMiniCart(false); navigate('/profile/cart'); }}
      />
      <div className="item-detail-container">
        <div className="item-detail-header">
          <button onClick={handleBackClick} className="back-btn">
            ← מכאן חוזרים
          </button>
        </div>

        <div className="item-detail-content">
          {/* Left side - Images */}
          <div className="item-detail-images">
            <div className="item-detail-main-image">
              {item.sale_price !== null && item.sale_price !== undefined && (
                <div className="item-detail-sale-badge">במבצע!</div>
              )}
              {mainImage ? (
                <img src={mainImage.url} alt={item.name} className='main-img'/>
              ) : (
                <div className="item-detail-no-image">למה אין תמונה?</div>
              )}
              {images.length > 1 && (
                <>
                  <button className="gallery-arrow gallery-arrow-left" onClick={prevImage}>
                    <ChevronLeft size={24} />
                  </button>
                  <button className="gallery-arrow gallery-arrow-right" onClick={nextImage}>
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Right side - Info */}
          <div className="item-detail-info">
            <div className="item-detail-description">
              {/* Title */}
              <div className='title-row'>
                {item.video && (
                  <Video size={26} color="#0cad5e" />
                )}
                <h1 className="item-title-large">{item.name}</h1>
              </div>

              {/* Description */}
              {item.description && (
                <div className="item-description-small">
                  <p>{item.description}</p>
                </div>
              )}
{/* 
              {user && (
                <button className="favorite-heart-btn" onClick={toggleFavorite}>
                  <Heart 
                    size={28} 
                    fill={isItemFavorite() ? '#e53935' : 'none'} 
                    color="#e53935"
                    strokeWidth={2}
                  />
                </button>
              )} */}

              {/* First row - Participants and Age */}
              <div className="info-row info-row-first">
                <div className="info-box info-box-age">
                  <div className="info-box-label">
<Cake size={20} />

</div>
                  <div className="info-box-value font-light" dangerouslySetInnerHTML={{ __html: item.age !== null && item.age !== undefined ? String(item.age) : 'לא צוין' }} />
                </div>
                <div className="info-box info-box-participants">
                  <div className="info-box-label"><Users size={20} /></div>
                  <div className="info-box-value">{item.much_for || 'לא צוין'}</div>
                </div>

              </div>

              {/* Second row - Add to Cart and Price */}
              <div className="info-row info-row-second">
                <button
                  className="info-box info-box-add-to-cart"
                  onClick={handleAddToCart}
                >
                  <div className="info-box-label bold">הוסף לסל</div>
                  <div className="info-box-value bold">←</div>
                </button>
                <div className="info-box info-box-price">
                  {item.sale_price !== null && item.sale_price !== undefined ? (
                    <div className="info-box-value bold price-container">
                      <span className="original-price">₪{item.price}</span>
                      <span className="sale-price">₪{item.sale_price}</span>
                    </div>
                  ) : (
                    <div className="info-box-value bold">₪{item.price}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section - Recommendations */}
        <div className="item-recommendations">
          <h3>המלצות עלי :)</h3>
          {recommendationsLoading ? (
            <div className="recommendations-loading">טוען המלצות...</div>
          ) : recommendations.length > 0 ? (
            <div className="recommendations-list">
              {recommendations.map((rec, index) => (
                <div 
                  key={rec.id || index} 
                  className={`recommendation-item ${index % 2 === 0 ? 'recommendation-right' : 'recommendation-left'}`}
                >
                  <div className="recommendation-name-box">
                    <div className="recommendation-name">{rec.name}</div>
                  </div>
                  <div className="recommendation-text-box">
                    <div className="recommendation-text">{rec.text}</div>
                  </div>
                  <div className="recommendation-white-box">
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="recommendations-placeholder">
              <p>המלצות עלי עדיין בדרך... אולי עוד תספיקי להיות הראשונה שממליצה עלי</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
